import React, { useCallback, useEffect, useId, useState } from "react";

/** Pulls the canonical error shapes apart so a denial reads as a sentence, not a stack trace. */
export function describeError(error: unknown): string {
  const response = (error as { response?: string })?.response;
  if (typeof response === "string" && response.length > 0) {
    try {
      const body = JSON.parse(response);
      if (body.message) {
        // The additive fields carry the interesting part of a modern denial (§8.2).
        const extra = [
          body.offending_audience_term ? `term: ${body.offending_audience_term}` : null,
          body.offending_value ? `value: ${body.offending_value}` : null,
          body.required_capability ? `needs: ${body.required_capability}` : null,
        ].filter(Boolean);
        return extra.length > 0 ? `${body.message} (${extra.join(", ")})` : body.message;
      }
    } catch {
      /* fall through */
    }
  }

  return (error as Error)?.message ?? "Something went wrong.";
}

/** Loads once, exposes a reload, and never leaves the caller guessing which state it is in. */
export function useAsync<T>(load: () => Promise<T>, deps: React.DependencyList = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      setData(await load());
      setError(null);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void run();
  }, [run]);

  return { data, error, loading, reload: run, setError };
}

export function Panel({ title, children, actions }: {
  title?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="panel">
      {(title || actions) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {title && <h3>{title}</h3>}
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * A labelled control.
 *
 * The label is wired to its control with a generated id rather than left floating beside it. An
 * unassociated label is invisible to a screen reader and unclickable — the caption reads as decoration
 * to anything that is not a pair of eyes. A child that brought its own id keeps it.
 */
export function Field({ label, children, style }: {
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const generated = useId();

  const single = React.isValidElement<{ id?: string }>(children) ? children : null;
  const id = single?.props.id ?? generated;

  return (
    <div className="field" style={style}>
      <label htmlFor={single ? id : undefined}>{label}</label>
      {single ? React.cloneElement(single, { id }) : children}
    </div>
  );
}

export function Alert({ kind, children }: { kind: "error" | "warn" | "ok" | "info"; children: React.ReactNode }) {
  return <div className={`alert ${kind}`}>{children}</div>;
}

export function Chip({ kind, children }: { kind?: "on" | "ok" | "warn" | "crit"; children: React.ReactNode }) {
  return <span className={`chip${kind ? ` ${kind}` : ""}`}>{children}</span>;
}

export function SeverityChip({ severity }: { severity?: string }) {
  const kind = severity === "critical" ? "crit" : severity === "warning" ? "warn" : undefined;
  return <Chip kind={kind}>{severity ?? "info"}</Chip>;
}

export function Stat({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="stat">
      <div className="k">{label}</div>
      <div className="n">{value}</div>
      {hint && <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

export function when(date?: string | null): string {
  if (!date) return "—";
  return new Date(date).toLocaleString();
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="muted" style={{ padding: "14px 2px" }}>{children}</p>;
}
